<template>
  <div class="container profile">
    <div v-if="student.initializing" class="row">
      <Loader style="width: 100%" />
    </div>

    <div v-else class="row">
      <div class="col-12 col-lg-4">
        <div class="dashboard-card profile-block">
          <div class="avatar">
            <div
              v-if="student.photoUrl"
              class="user-avatar"
              role="img"
              aria-label="Uploaded Avatar Photo"
              :style="{ backgroundImage: `url(${student.photoUrl})` }"
            />
            <div v-else class="user-avatar" />
            <h2>{{ student.fullName }}</h2>
            <table class="table bm-table mt-4">
              <tbody class="text-left">
                <tr>
                  <td class="col-7">Status:</td>
                  <td class="text-right">
                    <span v-if="isFaculty">
                      Staff
                    </span>
                    <span v-else-if="student.isFullTimeStudent">
                      Full Time Student
                    </span>
                    <span v-else>
                      Part Time Student
                    </span>
                  </td>
                </tr>

                <tr>
                  <td>Individual Credits Remaining:</td>
                  <td class="text-right">
                    {{ student.individualCreditsRemaining }}
                  </td>
                </tr>

                <tr v-if="!isFaculty">
                  <td>Wellbeing Workshop Credits Remaining:</td>
                  <td class="text-right">
                    {{ student.groupCreditsRemaining }}
                  </td>
                </tr>

                <tr v-if="!isFaculty && student.isInternationalStudent">
                  <td>International:</td>
                  <td class="text-right">Yes</td>
                </tr>
                <tr v-if="!isFaculty && student.isStudentAthlete">
                  <td>Athlete:</td>
                  <td class="text-right">Yes</td>
                </tr>
                <tr v-if="!isFaculty && student.studentIdNumber">
                  <td>Student Id:</td>
                  <td class="text-right">
                    {{ student.studentIdNumber }}
                  </td>
                </tr>
                <tr v-if="!isFaculty && student.classYear">
                  <td>Class Year:</td>
                  <td class="text-right">
                    {{ student.classYear }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="col-12 col-lg-8">
        <div class="dashboard-card light">
          <div class="row">
            <div class="col-8">
              <h2>Bio/About</h2>
            </div>
          </div>
          <p v-if="!student.bio">
            This user hasn't filled out their bio yet.
          </p>
          <p v-else>
            {{ student.bio }}
          </p>

          <h3 style="margin-top: 40px; margin-bottom: 20px; font-size: 1.5rem">
            More Info
          </h3>

          <table class="table bm-table">
            <tbody>
              <tr v-if="student.email">
                <td>Email:</td>
                <td class="text-right">
                  {{ student.email }}
                </td>
              </tr>
              <tr v-if="student.phoneNumber">
                <td>Phone:</td>
                <td class="text-right">
                  {{ student.phoneNumber }}
                </td>
              </tr>
              <tr v-if="student.gender">
                <td>Gender Identity:</td>
                <td class="text-right">
                  {{ getStudentGenderDisplayName(student.gender) }}
                </td>
              </tr>
              <tr>
                <td>Ethnicity:</td>
                <td class="text-right">
                  {{ student.ethnicity || "Preferred not to answer" | capitalize }}
                </td>
              </tr>
              <tr v-if="student.city">
                <td>Current City:</td>
                <td class="text-right">
                  {{ student.city }}, {{ student.state }}
                </td>
              </tr>
            </tbody>
          </table>

          <template v-if="student.struggles.length">
            <b class="d-block mt-4">
              I am Experiencing...
            </b>
            <ul class="pills">
              <li v-for="struggle in student.struggles" :key="struggle.id">
                {{ struggle.name }}
              </li>
            </ul>
          </template>
        </div>
        <div class="dashboard-card light">
          <session-history />
        </div>
      </div>
    </div>
    <send-message-modal ref="sendMessageModal" />
  </div>
</template>

<script>
import Loader from '@/components/Loader.vue';
import SendMessageModal from '@/components/modals/SendMessageModal.vue';
import SessionHistory from './components/SessionHistory.vue'
import StudentProfileModel from './StudentProfileModel'
import genderOptions from '@/helpers/mixins/gender-options';

export default {
  name: 'StudentProfile',
  components: {
    Loader,
    SendMessageModal,
    SessionHistory,
  },
  mixins: [genderOptions],
  provide() {
    return {
      student: this.student
    }
  },
  data() {
    return {
      student: new StudentProfileModel(this.$http),
      isFaculty: false,

    };
  },
  async mounted() {
    try {
      const { currentUser } = this.$store.getters;
      await this.student.initialize(currentUser.id);

      this.isFaculty = await this.$globals.schoolHasFeature('users-are-faculty', currentUser.school.id);
    } catch (ex) {
      console.error(ex);
    }
  }
};
</script>

<style lang="scss" scoped>
.profile {
  .avatar {
    text-align: center;

    .user-avatar {
      @include background-cover;
      @include border-radius(100px);

      background-image: url('~@/assets/img/default-user.jpg');
      height: 150px;
      width: 150px;
      margin-right: 15px;
      display: inline-block;
      vertical-align: top;
      margin-bottom: 15px;
    }

    p {
      opacity: 0.5;
    }
  }

  .info-list {
    list-style-type: none;
    padding-left: 0;
    font-size: 18px;
    margin-bottom: 0;

    i,
    svg {
      color: $green;
      margin-right: 8px;
    }

    li {
      margin-bottom: 5px;

      &:last-of-type {
        margin-bottom: 0;
      }
    }

    a {
      color: white;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .pills {
    margin-bottom: 0;
    margin-top: 15px;
  }

  .insurance-block {
    label {
      @include label;

      margin-bottom: 15px;
    }

    p {
      font-size: 18px;
      margin-bottom: 0;
    }
  }

  .icon-btn {
    border: none;
    background: none;
    padding: 0;
    color: $green;
    font-size: 20px;
    margin-right: 20px;

    .fa-calendar-plus {
      font-size: 18px;
    }

    .fa-envelope,
    .fa-phone-square {
      font-size: 20px;
      position: relative;
      top: 2px;
    }
  }

  p {
    white-space: pre-wrap;
    margin: 0px;
  }
}
</style>
